import _ from 'lodash';
import { comparer } from 'mobx';
import formatters from './formatters';
import { typedEntries } from './object';
import {
    FilterItem,
    FilterSnapshot,
    SearchFilterSnapshot,
} from './utilityTypes';

const getTagLabelCountingFromFilter = <T>(
    filter: FilterSnapshot<T>,
    selected?: T[]
): string => {
    const itemsSelectedCount = selected
        ? selected.length
        : filter?.selected?.length;

    if (!itemsSelectedCount) return '';

    const defaultValue = filter.selected[0];
    const option = filter.options.find(option => option.value === defaultValue);
    if (itemsSelectedCount === 1) return `${option?.label}`;

    return `${option?.label} e ${
        itemsSelectedCount - 1
    } ${formatters.numberInflection(
        itemsSelectedCount - 1,
        'outro',
        'outros'
    )}`;
};

const getFilterSearchLabel = <T>(
    searchByFilter: SearchFilterSnapshot<T>
): string => {
    const searchOption = searchByFilter.options.find(
        option => option.value === searchByFilter.searchType
    );
    if (searchOption) return searchOption.label;
    return '';
};

const showSearchFilter = <T>(
    searchByFilter: SearchFilterSnapshot<T>
): boolean => !_.isEmpty(searchByFilter.stringSearch);

const objectToFilterOptions = <T extends keyof unknown>(
    obj: Readonly<Record<T, string>>
): FilterItem<T>[] => {
    return typedEntries(obj).map(([key, value]) => ({
        label: value,
        value: key,
    }));
};

const getTabSelectedKey = <T, K extends string>(
    filter: FilterSnapshot<T>,
    keyToStatuses: Record<K, T[]>,
    isArchived = false
): K | 'archived' | '' => {
    if (isArchived) return 'archived';

    return (
        (Object.keys(keyToStatuses).find(key =>
            comparer.structural(keyToStatuses[key as K], filter.selected)
        ) as K) ?? ''
    );
};

type OptionFilter<T extends string> = Partial<Record<T, T[]>>;

const filterOptionAlias = <T extends string>(
    options: T[],
    filter: OptionFilter<T>
) => {
    const filterOut = Object.values<T[]>(filter as Record<T, T[]>).reduce(
        (prev, cur) => [...prev, ...cur]
    );
    return options.filter(o => !filterOut.includes(o));
};

const selectOptionAndAlias = <T extends string>(
    selected: T[],
    filter: OptionFilter<T>
) => {
    const keys = Object.keys(filter);
    return selected.reduce((prev, cur) => {
        return keys.includes(cur)
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              [...prev, cur, ...filter[cur]!]
            : [...prev, cur];
    }, [] as T[]);
};

export {
    filterOptionAlias,
    getFilterSearchLabel,
    getTabSelectedKey,
    getTagLabelCountingFromFilter,
    objectToFilterOptions,
    selectOptionAndAlias,
    showSearchFilter,
};
